import { useCallback, useState, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

/**
 * Tour hook for Employee Create form
 * Uses custom tour-styles for consistent styling across all tours
 */
export const useCreateEmployeeTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    const tourSteps: TourStep[] = [
        {
            selector: '[data-tour="employee-name"]',
            title: 'Tên nhân viên',
            description:
                'Nhập họ và tên đầy đủ của nhân viên.\n\nVí dụ: "Nguyễn Văn An"',
        },
        {
            selector: '[data-tour="employee-department"]',
            title: 'Phòng ban',
            description:
                'Chọn phòng ban mà nhân viên sẽ làm việc.\n\nPhòng ban xác định cơ cấu tổ chức và báo cáo.',
        },
        {
            selector: '[data-tour="employee-position"]',
            title: 'Chức vụ / Vị trí',
            description:
                'Chọn chức vụ của nhân viên trong phòng ban.\n\nDanh sách chức vụ sẽ thay đổi tùy theo phòng ban được chọn.',
        },
        {
            selector: '[data-tour="employee-type"]',
            title: 'Loại nhân viên',
            description:
                'Chọn loại hợp đồng:\n• Chính thức: Nhân viên full-time\n• Thử việc: Nhân viên trong thời gian thử việc\n• Part-time: Làm việc bán thời gian',
        },
        {
            selector: '[data-tour="employee-email"]',
            title: 'Email công ty',
            description:
                'Nhập email công ty của nhân viên.\n\nEmail này sẽ được dùng để:\n• Đăng nhập hệ thống\n• Nhận thông báo\n• Gửi link xác nhận tài khoản',
        },
        {
            selector: '[data-tour="employee-submit"]',
            title: 'Tạo tài khoản',
            description:
                'Nhấn "Tạo mới" để hoàn tất.\n\nHệ thống sẽ:\n• Tạo tài khoản tạm\n• Gửi email xác nhận cho nhân viên\n• Nhân viên điền thông tin chi tiết sau khi xác nhận',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                goToStep(stepIndex + 1)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 300)
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useCreateEmployeeTour
